package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.trf
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import kotlin.random.Random

/** Grip-only reordering keeps title fields editable and makes the same moves available on a keyboard. */
@Composable
fun <T> LReorderList(
    items: List<T>, key: (T) -> String, onMove: (from: Int, to: Int) -> Unit,
    label: String, itemLabel: (T) -> String, modifier: Modifier = Modifier,
    row: @Composable (item: T, gripModifier: Modifier) -> Unit,
) {
    val scope = remember { "reorder-${Random.nextInt(1_000_000)}" }
    val currentItems by rememberUpdatedState(items)
    val move by rememberUpdatedState(onMove)
    var keyboard by remember { mutableStateOf(ReorderState()) }
    var dragging by remember { mutableStateOf<String?>(null) }
    var pointerId by remember { mutableStateOf<Int?>(null) }
    var dropIndex by remember { mutableStateOf<Int?>(null) }
    var announcement by remember { mutableStateOf("") }
    val palette = ColorMode.current.toSitePalette()
    fun announce(message: String) { announcement = if (announcement == message) "$message " else message }
    fun moved(name: String, position: Int) = trf("ds.reorder.moved", "{item} moved to position {position} of {count}",
        "item" to name, "position" to (position + 1).toString(), "count" to currentItems.size.toString())
    DisposableEffect(scope) {
        val onPointerMove: (Event) -> Unit = listener@{ event ->
            if (dragging == null || event.asDynamic().pointerId != pointerId) return@listener
            val hit = document.elementFromPoint(event.asDynamic().clientX as Double, event.asDynamic().clientY as Double)
            val target = hit?.closest("[data-reorder-scope='$scope'][data-reorder-key]")
            dropIndex = currentItems.indexOfFirst { key(it) == target?.getAttribute("data-reorder-key") }.takeIf { it >= 0 }
            event.preventDefault()
        }
        val onPointerUp: (Event) -> Unit = listener@{ event ->
            if (event.asDynamic().pointerId != pointerId) return@listener
            val from = currentItems.indexOfFirst { key(it) == dragging }
            val target = dropIndex
            if (from >= 0 && target != null && from != target && target in currentItems.indices) {
                val name = itemLabel(currentItems[from])
                move(from, target)
                announce(moved(name, target))
            }
            dragging = null; pointerId = null; dropIndex = null
        }
        val cancel: (Event) -> Unit = { dragging = null; pointerId = null; dropIndex = null }
        val escape: (Event) -> Unit = { event ->
            if (event.asDynamic().key == "Escape" && dragging != null) {
                dragging = null; pointerId = null; dropIndex = null; event.preventDefault()
            }
        }
        window.addEventListener("blur", cancel)
        window.addEventListener("keydown", escape)
        window.addEventListener("pointermove", onPointerMove)
        window.addEventListener("pointerup", onPointerUp)
        window.addEventListener("pointercancel", cancel)
        onDispose {
            window.removeEventListener("blur", cancel)
            window.removeEventListener("keydown", escape)
            window.removeEventListener("pointermove", onPointerMove)
            window.removeEventListener("pointerup", onPointerUp)
            window.removeEventListener("pointercancel", cancel)
        }
    }
    Div(modifier.toAttrs { attr("role", "list"); attr("aria-label", label) }) {
        items.forEachIndexed { index, item ->
            key(key(item)) {
                Div(Modifier.styleModifier {
                    property("position", "relative")
                    if (dropIndex == index && dragging != null) property("box-shadow", "inset 0 2px ${palette.accent}")
                }.toAttrs {
                    attr("role", "listitem"); attr("data-reorder-scope", scope); attr("data-reorder-key", key(item))
                }) {
                    val grip = Modifier.styleModifier { property("touch-action", "none"); property("cursor", "grab") }.attrsModifier {
                        attr("data-reorder-grip", key(item))
                        attr("aria-pressed", (keyboard.position == index && keyboard.origin != null).toString())
                        onKeyDown { event ->
                            val result = reorderKey(keyboard, index, items.size, event.key, event.altKey)
                            if (result.handled) {
                                event.preventDefault()
                                val wasPicked = keyboard.origin != null
                                keyboard = result.state
                                result.move?.let { move(it.from, it.to) }
                                val message = when {
                                    event.key == "Escape" -> trf("ds.reorder.cancelled", "{item}, reorder cancelled", "item" to itemLabel(item))
                                    !wasPicked && result.state.origin != null -> trf("ds.reorder.picked", "{item}, picked up, position {position} of {count}",
                                        "item" to itemLabel(item), "position" to (index + 1).toString(), "count" to items.size.toString())
                                    result.move != null -> moved(itemLabel(item), result.move.to)
                                    else -> trf("ds.reorder.dropped", "{item}, dropped, position {position} of {count}",
                                        "item" to itemLabel(item), "position" to (index + 1).toString(), "count" to items.size.toString())
                                }
                                announce(message)
                            }
                        }
                        addEventListener("pointerdown") { event ->
                            if (event.nativeEvent.asDynamic().button == 0 && keyboard.origin == null && dragging == null) {
                                dragging = key(item); pointerId = event.nativeEvent.asDynamic().pointerId as Int; dropIndex = index
                                (event.currentTarget as? HTMLElement)?.focus()
                                event.preventDefault()
                            }
                        }
                    }
                    row(item, grip)
                }
            }
        }
        Div(LVisuallyHiddenStyle.toModifier().toAttrs { attr("role", "status"); attr("aria-live", "polite") }) { Text(announcement) }
    }
}
